// Copyright (c) Ping. All rights reserved.
//
// Attaches a LiveKit media Track to a DOM <video>. Web-only (uses
// Track.attach/detach + a real <video> element). With Room.adaptiveStream on,
// LiveKit only subscribes/sizes tracks for attached + visible elements — so
// mounting only the on-screen tiles is what keeps a 30-person call cheap.

import { useEffect, useRef } from 'react';

import type { Track } from 'livekit-client';

export interface CallVideoTileProps {
  readonly track?: Track | undefined;
  readonly mirror?: boolean;
  readonly className?: string;
  /** Use `contain` instead of `cover` — for screen shares, so the whole shared
   * surface is visible without cropping. */
  readonly contain?: boolean;
}

export function CallVideoTile({
  track,
  mirror = false,
  className,
  contain = false,
}: CallVideoTileProps): JSX.Element {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !track) return;
    track.attach(el);
    return () => {
      try {
        track.detach(el);
      } catch {
        /* element already gone */
      }
    };
  }, [track]);

  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      // Always muted: a <video> only carries the camera track here; remote
      // audio plays through dedicated <audio> sinks (LiveKitCallClient). An
      // unmuted <video> would be blocked by Safari/Chrome autoplay policy.
      muted
      className={className}
      style={{
        width: '100%',
        height: '100%',
        objectFit: contain ? 'contain' : 'cover',
        transform: mirror ? 'scaleX(-1)' : undefined,
      }}
    />
  );
}
